import { useEffect, useState } from 'react';
import { t, Trans } from '@lingui/macro';
import clsx from 'clsx';
import { ApplicationLoader } from 'components/ApplicationLoader/ApplicationLoader';
import { shootConfetti } from 'components/Confetti/shootConfetti';
import { InformationMessage } from 'components/InformationMessage/InformationMessage';
import { BaseLayout } from 'components/Layout/BaseLayout/BaseLayout';
import { InnerLayout } from 'components/Layout/InnerLayout/InnerLayout';
import { LayoutHeadings } from 'components/Layout/InnerLayout/LayoutHeadings/LayoutHeadings';
import { SideNavigation } from 'components/Navigation/SideNavigation';
import { useRejectReason } from 'components/RejectedOffer/useRejectReason';
import { SummaryWrapper } from 'components/SummaryCard/SummaryWrapper/SummaryWrapper';
import { useIntroducerContext } from 'contexts/IntroducerContext/IntroducerContext';
import { useLocale } from 'hooks/useLocale';
import {
  useGetDeclineReasons,
  useGetIntroducerApplication,
} from 'lendingApi/lendingApiHooks';
import { ToastContainer } from 'react-toastify';
import { TApplicationOverviewHeading } from 'types';
import { getApplicationOverviewHeadings } from 'utils/application/application.helpers';
import {
  APPLICATION_STATUS,
  APPLICATION_STATUS_VALUE,
} from 'utils/applications/applications.config';
import { REQUESTED_AMOUNT_CONTACT_THRESHOLD } from 'utils/applications/signup.config';
import styles from './application.module.css';

export const ApplicationOverviewPage = () => {
  const { isUK } = useLocale();
  const [headingsData, setHeadingsData] =
    useState<TApplicationOverviewHeading>();
  const { introducerId, checkIntroducerPermission } = useIntroducerContext();

  const {
    application,
    isLoading: isApplicationLoading,
    isFetching: isApplicationFetching,
  } = useGetIntroducerApplication();

  const rejectReason = useRejectReason(application);

  const { data: declineReasons } = useGetDeclineReasons(
    introducerId,
    application?.application_uid,
    application?.status,
  );

  const isPartnerChannel = application?.is_partner_channel || false;

  const showContactCustomerMessage =
    application?.status === APPLICATION_STATUS.awaiting_underwriter &&
    Number(application?.requested_products[0]?.amount) >=
      REQUESTED_AMOUNT_CONTACT_THRESHOLD &&
    isUK;

  const isFailedReintroduction =
    isUK &&
    application?.status === APPLICATION_STATUS.offered &&
    application?.commission_eligibility === 'FAILED_REINTRODUCTION';

  useEffect(() => {
    if (application?.status === 'offered') {
      setTimeout(() => shootConfetti('application-content'), 300);
    }
  }, [application?.status]);

  useEffect(
    () => {
      if (!application || isApplicationFetching) {
        return;
      }

      setHeadingsData(
        getApplicationOverviewHeadings({
          application,
          rejectReason,
          declineReasons: isUK ? declineReasons?.data : undefined,
          checkIntroducerPermission,
        }),
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      application,
      rejectReason,
      declineReasons?.data,
      isUK,
      isApplicationFetching,
    ],
  );

  const companyName = application?.company?.registered_company_name;

  return (
    <BaseLayout
      pageTitle={
        companyName
          ? `${companyName} - ${APPLICATION_STATUS_VALUE(application?.status)}`
          : t`Application | Introducer Portal`
      }
      className={styles.applicationLayout}
      contentPadding={false}
    >
      <SideNavigation />
      <InnerLayout
        headingComponent={
          <LayoutHeadings
            heading={headingsData?.heading}
            subHeading={headingsData?.subHeading}
            showManagedByIwocaPill={isPartnerChannel}
            className={clsx({
              [styles.hasInfoMessage]: showContactCustomerMessage,
            })}
          />
        }
        hasSidebar
        className={styles.application}
        maxWidth="min-content"
      >
        {!application ||
        isApplicationLoading ||
        !headingsData ||
        !introducerId ? (
          <ApplicationLoader />
        ) : (
          <>
            {showContactCustomerMessage && (
              <InformationMessage
                className={styles.informationMessage}
                message="For request amounts of £350,000 and above, our underwriting team may contact the applicant directly. We’ll notify you in advance."
              />
            )}
            {isFailedReintroduction && (
              <InformationMessage
                className={styles.informationMessage}
                message="The withdrawal fee and your commission are determined by the terms of the customer's initial funding."
              />
            )}
            <div className={styles.content} id="application-content">
              <SummaryWrapper applicationStatus={application.status} />
            </div>
          </>
        )}
        {isPartnerChannel && (
          <div className={styles.managedByIwocaFootnote}>
            <Trans>
              *Only the applicant can take actions on apps managed by iwoca.
            </Trans>
          </div>
        )}
      </InnerLayout>

      <ToastContainer position="bottom-right" />
    </BaseLayout>
  );
};
